<template>
  <div class="w-full h-full print:text-black" :style="wrapperStyle">
    <ApexCharts
      v-if="canRender"
      :key="chartKey"
      width="100%"
      height="100%"
      :type="type"
      :options="mergedOptions"
      :series="normalizedSeries"
    />
  </div>
</template>

<script>
import ApexCharts from "vue3-apexcharts";
import { nextTick } from "vue";

export default {
  name: "BaseChart",

  components: {
    ApexCharts,
  },

  props: {
    type: {
      type: String,
      required: true,
    },
    series: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    extraOptions: {
      type: Object,
      default: () => ({}),
    },

    // compatibilidade com versões onde o componente pai manda `options`
    options: {
      type: Object,
      default: () => ({}),
    },

    // compatibilidade com chamadas que enviam `height="320"`
    height: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      canRender: false,
      chartKey: 0,
    };
  },

  computed: {
    wrapperStyle() {
      if (!this.height) return {};

      const value =
        typeof this.height === "number"
          ? `${this.height}px`
          : String(this.height);

      return {
        minHeight: value,
        height: value,
      };
    },

    normalizedSeries() {
      return Array.isArray(this.series) ? this.series : [];
    },

    incomingOptions() {
      // prioriza extraOptions, mas aceita options também
      return {
        ...(this.options || {}),
        ...(this.extraOptions || {}),
      };
    },

    baseOptions() {
      return {
        chart: {
          background: "transparent",
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
          },
          parentHeightOffset: 0,
          redrawOnParentResize: true,
          redrawOnWindowResize: true,
        },

        theme: {
          mode: "dark",
        },

        grid: {
          borderColor: "rgba(255,255,255,0.05)",
          strokeDashArray: 4,
        },

        dataLabels: {
          enabled: false,
        },

        stroke: {
          width: 3,
          curve: "smooth",
        },

        xaxis: {
          categories: this.categories,
          labels: {
            style: {
              colors: "#9ca3af",
              fontSize: "12px",
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },

        yaxis: {
          labels: {
            style: {
              colors: "#9ca3af",
              fontSize: "12px",
            },
          },
        },

        legend: {
          labels: {
            colors: "#d1d5db",
          },
        },

        tooltip: {
          theme: "dark",
          style: {
            fontSize: "12px",
          },
        },

        responsive: [
          {
            breakpoint: 1024,
            options: {
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      };
    },

    mergedOptions() {
      const incoming = this.incomingOptions;

      return {
        ...this.baseOptions,
        ...incoming,

        chart: {
          ...this.baseOptions.chart,
          ...(incoming.chart || {}),
        },

        theme: {
          ...this.baseOptions.theme,
          ...(incoming.theme || {}),
        },

        grid: {
          ...this.baseOptions.grid,
          ...(incoming.grid || {}),
        },

        dataLabels: {
          ...this.baseOptions.dataLabels,
          ...(incoming.dataLabels || {}),
        },

        stroke: {
          ...this.baseOptions.stroke,
          ...(incoming.stroke || {}),
        },

        xaxis: {
          ...this.baseOptions.xaxis,
          ...(incoming.xaxis || {}),
          categories:
            incoming?.xaxis?.categories ||
            this.categories ||
            this.baseOptions.xaxis.categories,
          labels: {
            ...this.baseOptions.xaxis.labels,
            ...(incoming?.xaxis?.labels || {}),
            style: {
              ...this.baseOptions.xaxis.labels.style,
              ...(incoming?.xaxis?.labels?.style || {}),
            },
          },
        },

        yaxis: {
          ...this.baseOptions.yaxis,
          ...(incoming.yaxis || {}),
          labels: {
            ...this.baseOptions.yaxis.labels,
            ...(incoming?.yaxis?.labels || {}),
            style: {
              ...this.baseOptions.yaxis.labels.style,
              ...(incoming?.yaxis?.labels?.style || {}),
            },
          },
        },

        legend: {
          ...this.baseOptions.legend,
          ...(incoming.legend || {}),
          labels: {
            ...this.baseOptions.legend.labels,
            ...(incoming?.legend?.labels || {}),
          },
        },

        tooltip: {
          ...this.baseOptions.tooltip,
          ...(incoming.tooltip || {}),
          style: {
            ...this.baseOptions.tooltip.style,
            ...(incoming?.tooltip?.style || {}),
          },
        },
      };
    },
  },

  watch: {
    type() {
      this.rebuildChart();
    },
    series: {
      deep: true,
      handler() {
        this.rebuildChart();
      },
    },
    categories: {
      deep: true,
      handler() {
        this.rebuildChart();
      },
    },
    extraOptions: {
      deep: true,
      handler() {
        this.rebuildChart();
      },
    },
    options: {
      deep: true,
      handler() {
        this.rebuildChart();
      },
    },
  },

  async mounted() {
    await this.safeMount();
  },

  methods: {
    async safeMount() {
      this.canRender = false;
      await nextTick();
      requestAnimationFrame(() => {
        this.canRender = true;
      });
    },

    rebuildChart() {
      // evita corrida de render do apexcharts
      this.chartKey += 1;
    },
  },
};
</script>
